import React, { useEffect, useRef, useState, useCallback } from 'react';

const AudioPlayer = ({channel, roomId, user_name, setSpeaking}) => {

  // État des blobs reçus
  const [blobQueue, setBlobQueue] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stats, setStats] = useState({
    received: 0,
    processed: 0,
    queueSize: 0,
    currentlyPlaying: null
  });

  // Refs pour la gestion FIFO
  const blobQueueRef = useRef([]);
  const debugMode = useRef(false);
  const isProcessingRef = useRef(false);
  const audioContextRef = useRef(null);
  const currentSourceRef = useRef(null);
  const nextPlayTimeRef = useRef(0);
  const processingTimeoutRef = useRef(null);
  const receivedAudiosRef = useRef([])
  

  // Configuration
  const MAX_QUEUE_SIZE = 50; // Taille max pour éviter l'accumulation
  const PROCESSING_INTERVAL = 10; // Vérification toutes les 10ms

  // Initialisation du contexte audio
  useEffect(() => {

    const handleMessage = async ({room, username, stamp, audioSegment, audio_type, size}) => {
        if (!audioContextRef.current) {
          return
        }
        let to_play = true
        // in prod mode, the following line mus be uncommented
        to_play = username !== user_name
        if (room === roomId && to_play && ( ! receivedAudiosRef.current.includes(stamp))){

            receivedAudiosRef.current.push(stamp)
      // const audiotest = document.createElement('audio');
      // console.log('WebM:', audiotest.canPlayType('audio/webm'));

            const blob = new Blob([audioSegment], {type : audio_type})

            receiveBLob(blob, username)
            // receiveBLob(audioSegment, username)
            if (receivedAudiosRef.current.length > 300){
                receivedAudiosRef.current = receivedAudiosRef.current.slice(-50)
            }
        }
    };

    initAudioContext();
    startProcessingLoop();

    channel.on('phone broadcast' , handleMessage);

    return () => {
      if (audioContextRef.current) {
        // Fermer l'AudioContext
        // audioContextRef.current.stop();
       // audioContextRef.current.close().then(() => {
        console.log('🔴 AudioContext fermé');
       // });
        channel.off('phone broadcast' , handleMessage)
      }
    };



  }, []);

  const initAudioContext = async () => {
    if (audioContextRef.current != null) {
      return
    }
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      
      nextPlayTimeRef.current = audioContextRef.current.currentTime;
      console.log('✅ Contexte audio prêt');
    } catch (error) {
      console.error('❌ Erreur audio context:', error);
    }
  };

  // Fonction pour recevoir un nouveau blob
  const receiveBLob = useCallback((blob, username='peebody') => {
    if (!blob) return;

    const blobInfo = {
      id: Date.now() + Math.random(),
      blob: blob,
      timestamp: performance.now(),
      processed: false,
      username: username
    };

    // Mise à jour thread-safe de la queue
    blobQueueRef.current = [...blobQueueRef.current, blobInfo];
    
    // Limiter la taille de la queue
    if (blobQueueRef.current.length > MAX_QUEUE_SIZE) {
      console.warn('⚠️ Queue overflow, dropping oldest blob');
      blobQueueRef.current = blobQueueRef.current.slice(-MAX_QUEUE_SIZE);
    }

    // Mise à jour de l'état pour l'affichage
    setBlobQueue([...blobQueueRef.current]);
    setStats(prev => ({
      ...prev,
      received: prev.received + 1,
      queueSize: blobQueueRef.current.length
    }));

    // Démarrer le traitement si pas encore commencé
    if (!isProcessingRef.current && blobQueueRef.current.length >= 1) {
      startProcessing();
    }
  }, []);

  // Démarrer le traitement continu
  const startProcessing = useCallback(() => {
    if (isProcessingRef.current) return;
    
    isProcessingRef.current = true;
    setIsProcessing(true);
    console.log('🚀 Traitement FIFO démarré');
  }, []);

  // Boucle de traitement principal
  const startProcessingLoop = useCallback(() => {
    const processNext = async () => {
      if (!isProcessingRef.current || !audioContextRef.current) {
        processingTimeoutRef.current = setTimeout(processNext, PROCESSING_INTERVAL);
        return;
      }

      // Traiter le prochain blob si disponible
      if (blobQueueRef.current.length > 0) {
        const nextBlob = blobQueueRef.current[0];
        
        if (!nextBlob.processed) {
          await processBlobToAudio(nextBlob);
        }
      }

      // Programmer la prochaine itération
      processingTimeoutRef.current = setTimeout(processNext, PROCESSING_INTERVAL);
    };

    processNext();
  }, []);

  // Convertir et jouer un blob
  const processBlobToAudio = async (blobInfo) => {
    try {
      // Marquer comme en cours de traitement
      blobInfo.processed = true;
      setSpeaking(blobInfo.username + '...')

      setStats(prev => ({
        ...prev,
        currentlyPlaying: blobInfo.id
      }));

      // Convertir le blob en ArrayBuffer
      const arrayBuffer = await blobInfo.blob.arrayBuffer();
      
      // Décoder l'audio
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      
      // Créer la source audio
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      
      // Calculer le temps de lecture pour continuité parfaite
      const currentTime = audioContextRef.current.currentTime;
      const playTime = Math.max(currentTime, nextPlayTimeRef.current);
      
      // Programmer la lecture
      source.start(playTime);
      
      // Mettre à jour le temps pour le prochain blob (continuité parfaite)
      nextPlayTimeRef.current = playTime + audioBuffer.duration;
      
      // Gérer la fin de lecture
      source.onended = () => {
        // Retirer le blob traité de la queue
        setSpeaking('')
        blobQueueRef.current = blobQueueRef.current.slice(1);
        setBlobQueue([...blobQueueRef.current]);
        
        setStats(prev => ({
          ...prev,
          processed: prev.processed + 1,
          queueSize: blobQueueRef.current.length,
          currentlyPlaying: null
        }));

        // Arrêter le traitement si plus de blobs
        if (blobQueueRef.current.length === 0) {
          stopProcessing();
        }
      };
      
      currentSourceRef.current = source;
      
      console.log(`🎵 Blob ${blobInfo.id} en lecture, durée: ${audioBuffer.duration}s`);
      
    } catch (error) {
      console.error('❌ Erreur traitement blob:', error);
      
      // Retirer le blob défectueux
      blobQueueRef.current = blobQueueRef.current.slice(1);
      setBlobQueue([...blobQueueRef.current]);
      
      setStats(prev => ({
        ...prev,
        queueSize: blobQueueRef.current.length
      }));
    }
  };

  // Arrêter le traitement
  const stopProcessing = useCallback(() => {
    isProcessingRef.current = false;
    setIsProcessing(false);
    
    if (currentSourceRef.current) {
      try {
        currentSourceRef.current.stop();
      } catch (e) {
        // Source peut déjà être arrêtée
      }
    }
    
    console.log('⏹️ Traitement FIFO arrêté');
  }, []);

  // Nettoyer les ressources
  const cleanup = () => {
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }
    
    stopProcessing();
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
  };

  // Vider la queue manuellement
  const clearQueue = () => {
    stopProcessing();
    blobQueueRef.current = [];
    setBlobQueue([]);
    setStats({
      received: 0,
      processed: 0,
      queueSize: 0,
      currentlyPlaying: null
    });
    nextPlayTimeRef.current = audioContextRef.current?.currentTime || 0;
  };

  // Simuler la réception de blobs (pour test)
  const simulateBlob = async () => {
    // Créer un blob audio de test
    const audioContext = new AudioContext();
    const sampleRate = audioContext.sampleRate;
    const duration = 0.5; // 500ms
    const frameCount = sampleRate * duration;
    
    const audioBuffer = audioContext.createBuffer(1, frameCount, sampleRate);
    const channelData = audioBuffer.getChannelData(0);
    
    // Générer un ton simple
    const frequency = 440 + Math.random() * 200; // Fréquence variable
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = Math.sin(2 * Math.PI * frequency * i / sampleRate) * 0.3;
    }
    
    // Convertir en WAV blob (simulation)
    const wavData = audioBufferToWav(audioBuffer);
    const blob = new Blob([wavData], { type: 'audio/wav' });
    
    receiveBLob(blob);
  };

  // Conversion AudioBuffer -> WAV (utilitaire)
  const audioBufferToWav = (buffer) => {
    const length = buffer.length;
    const arrayBuffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(arrayBuffer);
    
    // En-tête WAV simplifié
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, buffer.sampleRate, true);
    view.setUint32(28, buffer.sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, length * 2, true);
    
    // Données audio
    const channelData = buffer.getChannelData(0);
    let offset = 44;
    for (let i = 0; i < length; i++) {
      const sample = Math.max(-1, Math.min(1, channelData[i]));
      view.setInt16(offset, sample * 0x7FFF, true);
      offset += 2;
    }
    
    return arrayBuffer;
  };

  if (! debugMode.current) {
    return null
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
        
      <h2>🎵 Lecteur FIFO de Blobs Audio</h2>
      
      {/* Contrôles */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={simulateBlob}
          style={{
            padding: '10px 20px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            marginRight: '10px',
            cursor: 'pointer'
          }}
        >
          ➕ Ajouter Blob Test
        </button>
        
        <button
          onClick={() => {
            // Ajouter plusieurs blobs rapidement
            for (let i = 0; i < 5; i++) {
              setTimeout(() => simulateBlob(), i * 100);
            }
          }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            marginRight: '10px',
            cursor: 'pointer'
          }}
        >
          🚀 Ajouter 5 Blobs
        </button>
        
        <button
          onClick={clearQueue}
          style={{
            padding: '10px 20px',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          🗑️ Vider Queue
        </button>
      </div>

      {/* Statistiques */}
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h3>📊 Statistiques FIFO</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
          <div>
            <strong>État:</strong> {isProcessing ? '🟢 Traitement actif' : '🔴 En attente'}
          </div>
          <div>
            <strong>Blobs reçus:</strong> {stats.received}
          </div>
          <div>
            <strong>Blobs traités:</strong> {stats.processed}
          </div>
          <div>
            <strong>Queue actuelle:</strong> {stats.queueSize} blobs
          </div>
        </div>
        {stats.currentlyPlaying && (
          <div style={{ marginTop: '10px', color: '#4CAF50' }}>
            🎵 En lecture: Blob {stats.currentlyPlaying}
          </div>
        )}
      </div>

      {/* Visualisation de la queue */}
      <div style={{
        backgroundColor: '#e3f2fd',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h3>📋 Queue FIFO ({blobQueue.length} blobs)</h3>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '5px',
          marginTop: '10px'
        }}>
          {blobQueue.slice(0, 10).map((blob, index) => (
            <div
              key={blob.id}
              style={{
                padding: '5px 10px',
                backgroundColor: index === 0 && isProcessing ? '#4CAF50' : '#2196F3',
                color: 'white',
                borderRadius: '15px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              {index === 0 && isProcessing ? '🎵' : '⏳'} #{index + 1}
            </div>
          ))}
          {blobQueue.length > 10 && (
            <div style={{
              padding: '5px 10px',
              backgroundColor: '#999',
              color: 'white',
              borderRadius: '15px',
              fontSize: '12px'
            }}>
              +{blobQueue.length - 10}
            </div>
          )}
        </div>
      </div>

      {/* Instructions d'intégration */}
      <div style={{
        backgroundColor: '#fff3cd',
        padding: '15px',
        borderRadius: '8px',
        border: '1px solid #ffeaa7'
      }}>
        <h3>🔧 Intégration avec vos sockets</h3>
        <pre style={{
          backgroundColor: '#f8f9fa',
          padding: '10px',
          borderRadius: '4px',
          fontSize: '12px',
          overflow: 'auto'
        }}>
{`// Dans votre composant
const socket = useWebSocket('ws://votre-serveur');

socket.onmessage = (event) => {
  const blob = new Blob([event.data], { type: 'audio/wav' });
  receiveBLob(blob); // Fonction fournie par ce composant
};`}
        </pre>
        
        <div style={{ marginTop: '10px', fontSize: '14px' }}>
          <strong>✨ Avantages:</strong>
          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
            <li>Lecture continue sans blancs</li>
            <li>Traitement FIFO strict</li>
            <li>Gestion automatique de la queue</li>
            <li>Monitoring en temps réel</li>
            <li>Récupération d'erreur automatique</li>
          </ul>
        </div>
      </div>
    </div>
)};

export default AudioPlayer;